/**
 * Pure client rules for the guarded product-merge commit.
 * No fetch/DOM: gating, acknowledgements, stable operation identity, outcome
 * classification. Mirrors poscloud/worker/src/product-merge-ops.ts.
 *
 * Own rules pinned here:
 * - execution stays closed unless the server says execution_available/capability
 * - owner maintenance acknowledgements are always explicit (never defaulted)
 * - an unknown commit outcome keeps the same operation_id; it is never replaced
 *   by a new id and never retried automatically
 */

export type MergeOperationPhase =
  | "prepared"
  | "locked"
  | "vendor_pending"
  | "needs_reconciliation"
  | "active"
  | "superseded"
  | "failed";

export type MergeStatus = {
  operation_id: string;
  phase: MergeOperationPhase;
  pair: {
    canonical_item_id: string;
    alias_item_id: string;
    canonical_variant_id: string;
    alias_variant_id: string;
  };
  store_id: string;
  stock: { canonical_preimage: number; alias_preimage: number; combined: number };
  locks: string[];
  aliases: Array<{ alias_variant_id: string; canonical_variant_id: string; status: string; created_at: string }>;
  vendor: { before_recorded: boolean; alias_item_recorded: boolean; after_recorded: boolean };
  error: { code: string | null; message: string | null };
  action_required: string | null;
  created_at: string;
  updated_at: string;
  activated_at: string | null;
  limitations: string[];
};

export type MergeCommitRequest = {
  operation_id: string;
  canonical_item_id: string;
  alias_item_ids: [string];
  preview_token: string;
  acknowledgements: {
    sales_stopped: true;
    devices_synced: true;
    canonical_promo_preserved?: true;
  };
};

/** 200 active returns MergeStatus; guarded 200/4xx/5xx returns an error envelope. */
export type MergeCommitResponse =
  | MergeStatus
  | {
      error?: string | null;
      code?: string | null;
      retryable?: boolean;
      cause?: string;
      missing_item_ids?: string[];
      blockers?: Array<{ code: string; message: string }>;
      status?: MergeStatus;
    };

export type MergeGateView = {
  compatible: boolean;
  execution_available: boolean;
  capability?: { available: boolean; reasons?: string[] };
  execution_token?: { preview_token: string; expires_at: string };
  canonicalPromo: boolean;
};

export type MergeAckState = {
  sales_stopped: boolean;
  devices_synced: boolean;
  promo: boolean;
};

/** Persisted recovery pointer. Never contains a preview token, acknowledgements or secrets. */
export type StoredMergeOperation = {
  operationId: string;
  canonicalId: string;
  aliasId: string;
  storeId?: string;
};

/** Legacy single-key pointer. Read for migration only; never overwritten or blindly deleted. */
export const MERGE_OPERATION_STORAGE_KEY = "poscloud.merge.pending.v1";
/** One key per account + operation, so concurrent tabs cannot erase each other's id. */
export const MERGE_OPERATION_STORAGE_PREFIX = "poscloud.merge.pending.v2|";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem"> &
  Partial<Pick<Storage, "key" | "length">>;

function defaultMergeStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function storageCredentials(storage: StorageLike | null): {
  telegramKey: string | null;
  adminSecret: string | null;
  sessionToken: string | null;
} {
  try {
    const telegram = globalThis.window?.Telegram?.WebApp;
    return {
      telegramKey: telegram?.initDataUnsafe?.user?.id
        ? `user:${telegram.initDataUnsafe.user.id}`
        : telegram?.initData || null,
      adminSecret: storage?.getItem("admin_secret") || null,
      sessionToken: storage?.getItem("session_token") || null,
    };
  } catch {
    return { telegramKey: null, adminSecret: null, sessionToken: null };
  }
}

/** Scope from the same identity sources the API auth header uses. Empty = unauthenticated. */
export function currentMergeStorageScope(): string {
  return mergeStorageScope(storageCredentials(defaultMergeStorage()));
}

/** Scope the pre-fix build used (raw rotating token). Only used to recognise legacy entries. */
export function currentMergeLegacyStorageScope(): string {
  return legacyMergeStorageScope(storageCredentials(defaultMergeStorage()));
}

/** FNV-1a 64-bit. Used only as a non-reversible account fingerprint, never as a secret store. */
function fingerprint(value: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = (1n << 64n) - 1n;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= BigInt(value.charCodeAt(i));
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, "0");
}

/**
 * Stable account identity for the session token. The server mints
 * `<base64(username)>.<ts>.<sig>`; the username prefix survives re-login while
 * the timestamp/signature rotate. Never store or return the token itself.
 */
function stableSessionIdentity(token: string): string {
  const parts = token.split(".");
  const prefix = parts[0]?.trim() || "";
  // Require the server's 3-part shape: short strings like "s1" are valid base64
  // but are not usernames, and two different tokens must never collapse.
  if (parts.length === 3 && prefix.length >= 4) {
    try {
      const b64 = prefix.replace(/-/g, "+").replace(/_/g, "/");
      const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      const username = new TextDecoder("utf-8", { fatal: true }).decode(bytes).trim();
      if (username) return `user:${username}`;
    } catch {
      // Not valid base64/UTF-8; fall through to the token itself.
    }
  }
  return `token:${token}`;
}

/**
 * Account/workspace scope for pending-operation pointers, mirroring the auth
 * header priority (Telegram → admin_secret → session_token). Empty = no identity,
 * in which case nothing may be persisted or sent. Session tokens are reduced to
 * the stable username, not the rotating signature.
 */
export function mergeStorageScope(input: {
  telegramKey?: string | null;
  adminSecret?: string | null;
  sessionToken?: string | null;
}): string {
  if (input.telegramKey) return `tg:${fingerprint(input.telegramKey)}`;
  if (input.adminSecret) return `adm:${fingerprint(input.adminSecret)}`;
  if (input.sessionToken) return `sess:${fingerprint(stableSessionIdentity(input.sessionToken))}`;
  return "";
}

/** Old rotating-token scope, kept only so legacy v1 pointers remain attributable. */
export function legacyMergeStorageScope(input: {
  telegramKey?: string | null;
  adminSecret?: string | null;
  sessionToken?: string | null;
}): string {
  if (input.telegramKey) return `tg:${fingerprint(input.telegramKey)}`;
  if (input.adminSecret) return `adm:${fingerprint(input.adminSecret)}`;
  if (input.sessionToken) return `sess:${fingerprint(input.sessionToken)}`;
  return "";
}

function validItemId(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.trim().length <= 200;
}

function validStoredOperation(operation: Record<string, any>): operation is StoredMergeOperation {
  const canonicalId = typeof operation.canonicalId === "string" ? operation.canonicalId : "";
  const aliasId = typeof operation.aliasId === "string" ? operation.aliasId : "";
  return (
    SAFE_OP_ID.test(String(operation.operationId || "")) &&
    validItemId(canonicalId) &&
    validItemId(aliasId) &&
    canonicalId.trim() !== aliasId.trim() &&
    (operation.storeId == null || validItemId(operation.storeId))
  );
}

function toStoredOperation(parsed: Record<string, any>): StoredMergeOperation {
  return {
    operationId: String(parsed.operationId),
    canonicalId: String(parsed.canonicalId).trim(),
    aliasId: String(parsed.aliasId).trim(),
    ...(parsed.storeId != null ? { storeId: String(parsed.storeId).trim() } : {}),
  };
}

function mergeOperationStorageKey(scope: string, operationId: string): string {
  return `${MERGE_OPERATION_STORAGE_PREFIX}${scope}|${operationId}`;
}

/**
 * Id list used only when the storage cannot enumerate keys (SSR/test shims).
 * Browser localStorage enumerates, so this key is never read or written there.
 * Missing list on a non-enumerable storage = unknown, never "empty".
 */
const LIST_ID = "~list";

function mergeListStorageKey(scope: string): string {
  return mergeOperationStorageKey(scope, LIST_ID);
}

function readListIds(scope: string, storage: StorageLike): string[] | null {
  try {
    const raw = storage.getItem(mergeListStorageKey(scope));
    if (!raw) return null;
    if (raw.length > 8192) return null;
    const parsed = JSON.parse(raw) as Record<string, any> | null;
    if (
      !parsed ||
      parsed.scope !== scope ||
      !Array.isArray(parsed.ids) ||
      !(parsed.ids as unknown[]).every((id) => SAFE_OP_ID.test(String(id)))
    ) {
      return null;
    }
    return (parsed.ids as unknown[]).map(String);
  } catch {
    return null;
  }
}

function updateListIds(scope: string, operationId: string, add: boolean, storage: StorageLike): void {
  // Real localStorage enumerates; the key scan is authoritative there.
  if (storageKeys(storage)) return;
  try {
    const current = readListIds(scope, storage) ?? [];
    const next = current.filter((id) => id !== operationId);
    if (add) next.push(operationId);
    storage.setItem(
      mergeListStorageKey(scope),
      JSON.stringify({ scope, ids: [...new Set(next)].sort() }),
    );
  } catch {
    // Hint only; the operation key holds the authoritative payload.
  }
}

/** localStorage is enumerable; a storage without length/key is treated as unreadable (fail closed). */
function storageKeys(storage: StorageLike): string[] | null {
  if (typeof storage.length !== "number" || typeof storage.key !== "function") return null;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (typeof key === "string") keys.push(key);
    }
    return keys;
  } catch {
    return null;
  }
}

export type MergeStorageSnapshot = {
  /** All unresolved operations for this account; never filtered down destructively. */
  operations: StoredMergeOperation[];
  /** Non-null when something for this account cannot be interpreted; new commits must stop. */
  blockedReason: string | null;
};

const UNREADABLE_MERGE_STORAGE =
  "Không đọc được bộ nhớ thao tác gộp trên trình duyệt này. Chưa được gửi yêu cầu gộp.";
const AMBIGUOUS_MERGE_STORAGE =
  "Có dữ liệu thao tác gộp chưa xác định hoặc không tương thích. Không gửi thao tác mới cho đến khi đối soát xong.";

/**
 * Read all pending pointers for one stable account scope. Entries for other
 * accounts, unknown versions and malformed payloads are preserved, never
 * deleted. Multiple valid pointers are returned so the caller can block.
 */
export function readMergeStorageSnapshot(
  scope: string,
  storage: StorageLike | null = defaultMergeStorage(),
  legacyScope = "",
): MergeStorageSnapshot {
  if (!scope || !storage) return { operations: [], blockedReason: UNREADABLE_MERGE_STORAGE };
  const keys = storageKeys(storage);
  // Non-enumerable storage: use the id hint; absent/invalid hint fails closed.
  const listIds = keys ? null : readListIds(scope, storage);
  if (!keys && listIds === null) {
    return { operations: [], blockedReason: UNREADABLE_MERGE_STORAGE };
  }
  const operations = new Map<string, StoredMergeOperation>();
  let blockedReason: string | null = null;
  const prefix = `${MERGE_OPERATION_STORAGE_PREFIX}${scope}|`;
  const listKey = mergeListStorageKey(scope);
  const candidateKeys = new Set<string>();
  for (const key of keys ?? []) {
    if (key.startsWith(prefix) && key !== listKey) candidateKeys.add(key);
  }
  for (const id of listIds ?? []) candidateKeys.add(mergeOperationStorageKey(scope, id));
  for (const key of candidateKeys) {
    const keyOperationId = key.slice(prefix.length);
    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      blockedReason = UNREADABLE_MERGE_STORAGE;
      continue;
    }
    if (!raw) continue;
    if (raw.length > 4096) {
      blockedReason = AMBIGUOUS_MERGE_STORAGE;
      continue;
    }
    let parsed: Record<string, any> | null;
    try {
      parsed = JSON.parse(raw) as Record<string, any> | null;
    } catch {
      blockedReason = AMBIGUOUS_MERGE_STORAGE;
      continue;
    }
    if (
      !parsed ||
      typeof parsed !== "object" ||
      parsed.version !== 2 ||
      parsed.scope !== scope ||
      parsed.operationId !== keyOperationId ||
      !validStoredOperation(parsed)
    ) {
      blockedReason = AMBIGUOUS_MERGE_STORAGE;
      continue;
    }
    operations.set(keyOperationId, toStoredOperation(parsed));
  }
  // Legacy single key: only entries provably belonging to this account are used.
  try {
    const raw = storage.getItem(MERGE_OPERATION_STORAGE_KEY);
    if (raw) {
      if (raw.length > 4096) {
        blockedReason = AMBIGUOUS_MERGE_STORAGE;
      } else {
        let parsed: Record<string, any> | null = null;
        try {
          parsed = JSON.parse(raw) as Record<string, any> | null;
        } catch {
          blockedReason = AMBIGUOUS_MERGE_STORAGE;
        }
        if (parsed && typeof parsed === "object") {
          const belongs =
            parsed.scope === scope || (!!legacyScope && parsed.scope === legacyScope);
          if (belongs) {
            if (parsed.version === 1 && validStoredOperation(parsed)) {
              operations.set(String(parsed.operationId), toStoredOperation(parsed));
            } else {
              blockedReason = AMBIGUOUS_MERGE_STORAGE;
            }
          } else {
            // One shared legacy key that cannot be attributed to the active
            // identity: leave it untouched, but do not start a new commit that
            // might duplicate an unresolved operation.
            blockedReason = AMBIGUOUS_MERGE_STORAGE;
          }
        }
      }
    }
  } catch {
    blockedReason = UNREADABLE_MERGE_STORAGE;
  }
  return {
    operations: [...operations.values()].sort((a, b) => a.operationId.localeCompare(b.operationId)),
    blockedReason,
  };
}

/** Backward-compatible single read: first pending pointer, or null. */
export function readStoredMergeOperation(
  scope: string,
  storage: StorageLike | null = defaultMergeStorage(),
  legacyScope = "",
): StoredMergeOperation | null {
  return readMergeStorageSnapshot(scope, storage, legacyScope).operations[0] ?? null;
}

/**
 * Persist before any commit request. Returns false when the pointer could not be
 * stored and verified — callers MUST skip the request in that case. Each
 * account+operation has its own key, so a write never erases another tab's id.
 */
export function writeStoredMergeOperation(
  operation: StoredMergeOperation,
  scope: string,
  storage: StorageLike | null = defaultMergeStorage(),
): boolean {
  if (!scope || !storage || !validStoredOperation(operation)) return false;
  const raw = JSON.stringify({
    version: 2,
    scope,
    operationId: operation.operationId,
    canonicalId: operation.canonicalId.trim(),
    aliasId: operation.aliasId.trim(),
    ...(operation.storeId != null ? { storeId: operation.storeId.trim() } : {}),
  });
  const key = mergeOperationStorageKey(scope, operation.operationId);
  try {
    storage.setItem(key, raw);
    if (storage.getItem(key) !== raw) return false;
    updateListIds(scope, operation.operationId, true, storage);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear exactly the expected account+operation pointer. Never clears a shared
 * key, another account's entry, a different operation, or unparseable data.
 */
export function clearStoredMergeOperation(
  scope: string,
  operationId: string,
  storage: StorageLike | null = defaultMergeStorage(),
  legacyScope = "",
): boolean {
  if (!scope || !storage || !SAFE_OP_ID.test(operationId)) return false;
  let cleared = false;
  try {
    const key = mergeOperationStorageKey(scope, operationId);
    const raw = storage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, any> | null;
      if (
        parsed &&
        parsed.version === 2 &&
        parsed.scope === scope &&
        parsed.operationId === operationId
      ) {
        storage.removeItem(key);
        updateListIds(scope, operationId, false, storage);
        cleared = true;
      }
    }
  } catch {
    // Preserve anything unreadable; the caller still keeps the id.
  }
  try {
    const raw = storage.getItem(MERGE_OPERATION_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, any> | null;
      if (
        parsed &&
        parsed.version === 1 &&
        parsed.operationId === operationId &&
        (parsed.scope === scope || (!!legacyScope && parsed.scope === legacyScope))
      ) {
        storage.removeItem(MERGE_OPERATION_STORAGE_KEY);
        cleared = true;
      }
    }
  } catch {
    // Preserve legacy/future/corrupt entries.
  }
  return cleared;
}

export type MergeOutcomeClass =
  | { kind: "active"; keepOperationId: false; message: string }
  | { kind: "pending"; keepOperationId: true; message: string }
  | { kind: "retryable"; keepOperationId: true; message: string }
  | { kind: "needs_reconciliation"; keepOperationId: true; message: string }
  | { kind: "failed"; keepOperationId: boolean; message: string }
  | { kind: "unknown"; keepOperationId: true; message: string }
  | { kind: "stale_preview"; keepOperationId: true; message: string }
  | { kind: "acknowledgement_required"; keepOperationId: true; message: string }
  | { kind: "disabled"; keepOperationId: true; message: string }
  | { kind: "ineligible"; keepOperationId: false; message: string }
  | { kind: "busy"; keepOperationId: true; message: string }
  | { kind: "closed"; keepOperationId: false; message: string };

const SAFE_OP_ID = /^[A-Za-z0-9._:-]{8,100}$/;

export const MERGE_OFFLINE_RISK =
  "Không thể phát hiện hàng đợi offline của POS gốc. Nếu còn giao dịch offline chưa đồng bộ, tồn kho có thể lệch sau khi gộp; hãy tạm dừng bán và đồng bộ mọi thiết bị trước.";

/**
 * Server execution is closed. Map raw capability reasons to owner-facing copy;
 * never echo env/secret names, and never claim data changed (preview is read-only).
 */
export function mergeExecutionUnavailableReason(
  capability?: MergeGateView["capability"],
): string {
  const reasons = (capability?.reasons || []).filter(Boolean).join(" ").toLowerCase();
  if (reasons.includes("schema missing")) {
    return "Chưa kích hoạt gộp trên máy chủ: thiếu bảng dữ liệu gộp. Hiện chỉ xem trước, chưa thay đổi dữ liệu.";
  }
  if (reasons.includes("not configured")) {
    return "Chưa cấu hình đủ để gộp trên máy chủ. Hiện chỉ xem trước, chưa thay đổi dữ liệu.";
  }
  return "Chưa bật gộp trên máy chủ; hiện chỉ xem trước, chưa thay đổi dữ liệu.";
}

/** null = allowed. Used both to render the reason and to guard the button. */
export function mergeExecutionBlockedReason(gate: MergeGateView | null): string | null {
  if (!gate) return "Chưa có bản xem trước.";
  if (!gate.compatible) return "Cặp sản phẩm còn điều kiện chặn.";
  if (gate.execution_available !== true || !gate.capability?.available) {
    return mergeExecutionUnavailableReason(gate.capability);
  }
  if (!gate.execution_token?.preview_token) return "Bản xem trước chưa cấp mã thực thi; tải lại.";
  return null;
}

export function mergeAcksSatisfied(acks: MergeAckState, canonicalPromo: boolean): boolean {
  return (
    acks.sales_stopped === true &&
    acks.devices_synced === true &&
    (!canonicalPromo || acks.promo === true)
  );
}

/** Returns null instead of an invalid body; never sends defaulted acknowledgements. */
export function buildMergeCommitBody(input: {
  operationId: string;
  canonicalItemId: string;
  aliasItemId: string;
  previewToken: string;
  canonicalPromo: boolean;
  acks: MergeAckState;
}): MergeCommitRequest | null {
  const operationId = input.operationId.trim();
  if (!SAFE_OP_ID.test(operationId)) return null;
  if (!input.canonicalItemId || !input.aliasItemId) return null;
  if (input.canonicalItemId === input.aliasItemId) return null;
  if (!input.previewToken) return null;
  if (!mergeAcksSatisfied(input.acks, input.canonicalPromo)) return null;
  return {
    operation_id: operationId,
    canonical_item_id: input.canonicalItemId,
    alias_item_ids: [input.aliasItemId],
    preview_token: input.previewToken,
    acknowledgements: {
      sales_stopped: true,
      devices_synced: true,
      ...(input.acks.promo ? { canonical_promo_preserved: true as const } : {}),
    },
  };
}

export function createMergeOperationId(): string {
  const candidate =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `merge-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  // ponytail: UUID/fallback both satisfy the server regex; no custom alphabet needed.
  return candidate;
}

/**
 * Reuse the id for the SAME pair; only a different pair gets a new id.
 * Callers clear the stored operation on definite terminal failures, so an
 * unknown outcome can never be re-run under a fresh id.
 */
export function mergeOperationIdForAttempt(
  previous: { operationId: string; canonicalId: string; aliasId: string } | null,
  canonicalId: string,
  aliasId: string,
): string {
  if (
    previous &&
    previous.canonicalId === canonicalId &&
    previous.aliasId === aliasId &&
    SAFE_OP_ID.test(previous.operationId)
  ) {
    return previous.operationId;
  }
  return createMergeOperationId();
}

/** Server error/blocker strings are never dropped in favour of a generic status line. */
function payloadNotes(p: Record<string, any>): string {
  const blockers = Array.isArray(p.blockers)
    ? p.blockers.map((b: any) => (typeof b?.message === "string" ? b.message : "")).filter(Boolean)
    : [];
  return [typeof p.error === "string" ? p.error : "", ...blockers].filter(Boolean).join(" — ");
}

/**
 * A pending operation pinned to a different pair must be reconciled first; it is
 * never silently overwritten by a new pair (a reload could otherwise orphan it).
 */
export function mergePendingPairConflict(
  pending: StoredMergeOperation | null,
  canonicalId: string,
  aliasId: string,
): string | null {
  if (!pending) return null;
  const samePair =
    (pending.canonicalId === canonicalId && pending.aliasId === aliasId) ||
    (pending.canonicalId === aliasId && pending.aliasId === canonicalId);
  if (samePair) return null;
  return "Còn thao tác gộp chưa xác định cho cặp sản phẩm khác. Kiểm tra trạng thái thao tác đó trước.";
}

export type PrepareMergeCommitInput = {
  pending: StoredMergeOperation | null;
  canonicalItemId: string;
  aliasItemId: string;
  previewToken: string;
  canonicalPromo: boolean;
  acks: MergeAckState;
  storeId?: string | null;
  scope: string;
  /** Pre-fix rotating-token scope, accepted only to recognise legacy v1 pointers. */
  legacyScope?: string;
  storage?: StorageLike | null;
};

export type PrepareMergeCommitResult =
  | { ok: true; body: MergeCommitRequest; operation: StoredMergeOperation }
  | { ok: false; reason: string };

/**
 * Single guard before any commit request: pair ownership, explicit acks, and the
 * durable recovery pointer. The id is written and verified BEFORE the caller may
 * send; if it cannot be stored, nothing is sent.
 */
export function prepareMergeCommit(input: PrepareMergeCommitInput): PrepareMergeCommitResult {
  // Re-read storage: React state can be stale (other tab wrote a pointer, reload
  // recovered another id, account switched). Never trust the caller's copy.
  const storage = input.storage ?? defaultMergeStorage();
  const snapshot = readMergeStorageSnapshot(input.scope, storage, input.legacyScope ?? "");
  if (snapshot.blockedReason) return { ok: false, reason: snapshot.blockedReason };
  // Two unresolved operations (e.g. two tabs) is ambiguous: reconcile first.
  if (snapshot.operations.length > 1) return { ok: false, reason: AMBIGUOUS_MERGE_STORAGE };
  const stored = snapshot.operations[0] ?? null;
  // Storage is authoritative when present; otherwise the caller's id is the only
  // known pending attempt (still written+verified before sending).
  const authoritative = stored ?? input.pending;
  const pairConflict = mergePendingPairConflict(authoritative, input.canonicalItemId, input.aliasItemId);
  if (pairConflict) return { ok: false, reason: pairConflict };
  if (
    authoritative &&
    (authoritative.canonicalId !== input.canonicalItemId ||
      authoritative.aliasId !== input.aliasItemId)
  ) {
    // Same two items, opposite canonical: a different write. Reconcile first.
    return {
      ok: false,
      reason: "Thao tác chưa xác định đã dùng chiều sản phẩm chính khác. Kiểm tra trạng thái trước.",
    };
  }
  // Reuse the durable id, never mint a second one.
  const operationId = mergeOperationIdForAttempt(
    authoritative,
    input.canonicalItemId,
    input.aliasItemId,
  );
  const body = buildMergeCommitBody({
    operationId,
    canonicalItemId: input.canonicalItemId,
    aliasItemId: input.aliasItemId,
    previewToken: input.previewToken,
    canonicalPromo: input.canonicalPromo,
    acks: input.acks,
  });
  if (!body) return { ok: false, reason: "Chưa đủ xác nhận hoặc thiếu mã thực thi." };
  const operation: StoredMergeOperation = {
    operationId,
    canonicalId: input.canonicalItemId,
    aliasId: input.aliasItemId,
    ...(input.storeId ? { storeId: input.storeId } : {}),
  };
  if (!writeStoredMergeOperation(operation, input.scope, storage)) {
    return {
      ok: false,
      reason:
        "Không lưu được mã thao tác trên trình duyệt này. Chưa gửi yêu cầu gộp — mở lại trang và thử lại.",
    };
  }
  return { ok: true, body, operation };
}

export function classifyMergeStatus(status: MergeStatus): MergeOutcomeClass {
  switch (status.phase) {
    case "active":
      return { kind: "active", keepOperationId: false, message: "Đã gộp xong." };
    case "failed":
      return {
        kind: "failed",
        // Server-declared terminal phase: the vendor write was rejected and locks
        // were released, so a fresh preview/operation is required.
        keepOperationId: false,
        message: "Thao tác đã dừng an toàn. Tải lại bản xem trước rồi bắt đầu thao tác mới.",
      };
    case "needs_reconciliation":
      return {
        kind: "needs_reconciliation",
        keepOperationId: true,
        message:
          status.action_required ||
          "Trạng thái máy chủ gốc chưa xác định. Không thử lại; cần đối soát tồn kho trước khi bán tiếp.",
      };
    case "prepared":
      return {
        kind: "retryable",
        keepOperationId: true,
        message: "Thao tác đã tạo nhưng chưa khoá. Tải lại bản xem trước rồi thử lại với cùng mã.",
      };
    case "locked":
    case "vendor_pending":
      return {
        kind: "pending",
        keepOperationId: true,
        message: "Thao tác đang chờ xác nhận. Kiểm tra lại trạng thái với cùng mã.",
      };
    case "superseded":
      return { kind: "closed", keepOperationId: false, message: "Thao tác đã bị thay thế." };
    default:
      return {
        kind: "unknown",
        keepOperationId: true,
        message: "Trạng thái thao tác không xác định. Kiểm tra lại với cùng mã.",
      };
  }
}

function classifyStatusPhase(phase: string): MergeOutcomeClass {
  const base: MergeStatus = {
    operation_id: "",
    phase: phase as MergeOperationPhase,
    pair: { canonical_item_id: "", alias_item_id: "", canonical_variant_id: "", alias_variant_id: "" },
    store_id: "",
    stock: { canonical_preimage: 0, alias_preimage: 0, combined: 0 },
    locks: [],
    aliases: [],
    vendor: { before_recorded: false, alias_item_recorded: false, after_recorded: false },
    error: { code: null, message: null },
    action_required: null,
    created_at: "",
    updated_at: "",
    activated_at: null,
    limitations: [],
  };
  return classifyMergeStatus(base);
}

/** Duck-typed: ApiError carries { status: number, payload }; timeouts carry neither. */
export function classifyMergeFailure(error: unknown): MergeOutcomeClass {
  const http = Number((error as any)?.status);
  const payload = (error as any)?.payload;
  if (!payload || typeof payload !== "object") {
    return {
      kind: "unknown",
      keepOperationId: true,
      message:
        "Không nhận được xác nhận từ máy chủ. Thao tác có thể đã chạy — kiểm tra trạng thái với cùng mã, không gửi lại bằng mã mới.",
    };
  }
  return classifyMergePayload(payload, http);
}

/** A 200 can still carry a guarded error envelope, so success bodies go through here too. */
export function classifyMergePayload(payload: unknown, http = 200): MergeOutcomeClass {
  if (!payload || typeof payload !== "object") {
    return {
      kind: "unknown",
      keepOperationId: true,
      message: "Phản hồi máy chủ không đọc được. Kiểm tra trạng thái với cùng mã.",
    };
  }
  const p = payload as Record<string, any>;
  if (typeof p.phase === "string") return classifyStatusPhase(p.phase);
  if (p.status && typeof p.status.phase === "string") {
    const cls = classifyStatusPhase(String(p.status.phase));
    if (cls.kind === "needs_reconciliation") {
      // Server action/error strings win over the generic reconciliation line.
      const detail = [p.status.action_required, payloadNotes(p)].filter(Boolean).join(" — ");
      return detail ? { ...cls, message: String(detail) } : cls;
    }
    return cls;
  }
  const code = String(p.code || "");
  switch (code) {
    case "STALE_PREVIEW":
    case "STALE_STOCK":
      return {
        kind: "stale_preview",
        keepOperationId: true,
        message: "Bản xem trước đã cũ hoặc dữ liệu đã đổi. Tải lại và xác nhận lại từ đầu.",
      };
    case "CANONICAL_PROMO_ACK_REQUIRED":
      return {
        kind: "acknowledgement_required",
        keepOperationId: true,
        message: "Cần xác nhận giữ khuyến mãi của sản phẩm chính.",
      };
    case "MERGE_EXECUTION_DISABLED":
    case "MERGE_NOT_CONFIGURED":
      // 503 is emitted before the body/operation is examined, but a proxy or an
      // older deployment can answer it after the commit reached a worker. Keep the
      // durable pointer so the owner can check status with the same id; never mint
      // a new id or resend automatically.
      return { kind: "disabled", keepOperationId: true, message: "Máy chủ chưa cho phép thực thi gộp. Kiểm tra trạng thái với cùng mã." };
    case "MERGE_LOCKED":
    case "MERGE_PAIR_BUSY":
      return {
        kind: "busy",
        keepOperationId: true,
        message: "Đang có thao tác gộp khác trên sản phẩm này. Thử lại sau.",
      };
    case "ITEM_NOT_FOUND":
    case "MERGE_NOT_COMPATIBLE":
      return {
        kind: "ineligible",
        keepOperationId: false,
        message: payloadNotes(p) || "Cặp sản phẩm không còn đủ điều kiện; tải lại bản xem trước.",
      };
    case "NEEDS_RECONCILIATION":
      return {
        kind: "needs_reconciliation",
        keepOperationId: true,
        message:
          "Kết quả phía máy chủ gốc chưa xác định. Không thử lại; cần đối soát tồn kho và trạng thái bán.",
      };
    case "OPERATION_NOT_RETRYABLE":
      return {
        kind: "closed",
        keepOperationId: false,
        message: String(p.error || "Thao tác trước không thể chạy lại. Tải lại bản xem trước."),
      };
    default:
      if (Number.isFinite(http) && http >= 500) {
        return {
          kind: "unknown",
          keepOperationId: true,
          message:
            "Máy chủ báo lỗi sau khi nhận thao tác. Không gửi lại — kiểm tra trạng thái với cùng mã.",
        };
      }
      return {
        kind: "failed",
        // A generic 4xx does not prove the server skipped the operation. Keeping the
        // same id is always safe and blocks an accidental duplicate commit.
        keepOperationId: true,
        message: payloadNotes(p) || "Thao tác gộp thất bại; kiểm tra trạng thái trước khi thử lại.",
      };
  }
}
